import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronUp, Loader2, TicketPercent } from "lucide-react";
import { COUPON_CATEGORY_I18N_KEYS } from "@/utils/businessCoupon";

export type StudentCoupon = {
  id: string;
  offer_type?: string | null;
  coupon_image?: string | null;
  coupon_name?: string | null;
  coupon_category?: string | null;
  original_price?: number | string | null;
  redeem_point?: number | null;
  issued_quantity?: number | string | null;
  terms_conditions_en?: string | null;
  terms_conditions_zh_hant?: string | null;
  expiry_date?: string | null;
  /**
   * The owning business is no longer in good standing (deactivated, deleted or
   * its subscription ended). The coupon is still listed — the student may have
   * bought it before — but the card says so.
   */
  is_business_inactive?: boolean | number | null;
};

interface StudentCouponCardProps {
  coupon: StudentCoupon;
  onRedeem?: (couponId: string) => void;
  pending?: boolean;
}

/**
 * Coupon card mirroring the RN `AnimatedCouponCard` front face: image, name,
 * category and original price, with a `Redeem` action. Redeem points and the
 * issued quantity are deliberately not shown (the RN card computes them but
 * never renders them).
 *
 * A coupon whose business is no longer in good standing keeps its place in the
 * list: the offer was published while the business was live, so hiding it would
 * hide a real redemption. The card itself does not advertise the suspension;
 * tapping `Redeem` explains why the offer cannot be redeemed.
 */
const StudentCouponCard: React.FC<StudentCouponCardProps> = ({
  coupon,
  onRedeem,
  pending = false,
}) => {
  const { t, i18n } = useTranslation();
  const [showTerms, setShowTerms] = useState(false);
  const isZh = String(i18n?.language ?? "").toLowerCase().startsWith("zh");
  const name = String(coupon?.coupon_name ?? "");
  const image = String(coupon?.coupon_image ?? "");
  const categoryCode = String(coupon?.coupon_category ?? "").trim();
  const category = categoryCode
    ? t(COUPON_CATEGORY_I18N_KEYS[categoryCode] ?? categoryCode)
    : "";
  const terms = String(
    isZh
      ? coupon?.terms_conditions_zh_hant || coupon?.terms_conditions_en || ""
      : coupon?.terms_conditions_en || coupon?.terms_conditions_zh_hant || "",
  ).trim();
  const originalPrice = coupon?.original_price;

  return (
    <article className="flex h-full flex-col overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm transition hover:shadow-md">
      <div className="flex items-center gap-3 p-4">
        <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-gray-50">
          {image ? (
            <img
              src={image}
              alt={name || t("Coupon")}
              loading="lazy"
              className="h-full w-full object-contain"
            />
          ) : (
            <TicketPercent size={24} className="text-gray-300" />
          )}
        </div>

        <div className="min-w-0 flex-1">
          <h3 className="line-clamp-2 text-sm font-bold leading-5 text-gray-900">
            {name || t("Coupon")}
          </h3>
          {category && (
            <p className="mt-0.5 line-clamp-1 text-xs text-gray-500">{category}</p>
          )}
          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
            {originalPrice ? (
              <span className="font-bold text-[#ffa217]">
                {t("Original Price")}: {String(originalPrice)} {t("HKD")}
              </span>
            ) : null}
          </div>
        </div>
      </div>

      {terms && (
        <div className="px-4">
          <p
            className={`text-xs leading-4 text-gray-400 ${
              showTerms ? "" : "line-clamp-2"
            }`}
          >
            {terms}
          </p>
          <button
            type="button"
            onClick={() => setShowTerms((value) => !value)}
            className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-[#ffa217]"
          >
            {showTerms ? (
              <>
                {t("Terms & Conditions")}
                <ChevronUp size={12} />
              </>
            ) : (
              t("Tap to view terms")
            )}
          </button>
        </div>
      )}

      <div className="mt-auto flex items-center gap-2 px-4 py-3">
        <button
          type="button"
          onClick={() => onRedeem?.(coupon.id)}
          disabled={pending}
          className="ml-auto inline-flex shrink-0 items-center gap-1.5 rounded-xl bg-[#ffa217] px-4 py-2 text-xs font-bold text-white transition hover:bg-[#f59200] disabled:opacity-60"
        >
          {pending && <Loader2 size={13} className="animate-spin" />}
          {t("Redeem")}
        </button>
      </div>
    </article>
  );
};

export default StudentCouponCard;
